<script setup lang="ts">
import { SUPPORTED_MIME_TYPES } from '@/backend/types'
import { useProjectNavigator } from '@/modules/Cases/Sidebar/useProjectNavigator'
import { provideProjectNavigationContext } from '@/modules/Project/Navigation/projectNavigationContext'
import { invariant } from '@/shared/utils/typeAssertions'
import ScrollGradient from '@/sharedComponents/ScrollGradient.vue'
import { useDropzone } from '@/sharedComposables/useDropzone'
import DarwinButton from '@/uiKit/DarwinButton.vue'
import IconSprite from '@/uiKit/IconSprite.vue'
import { syncRefs, useElementSize, useElementVisibility, useLocalStorage } from '@vueuse/core'
import {
  computed,
  nextTick,
  ref,
  toRef,
  useTemplateRef,
  watch,
  type ComponentPublicInstance,
} from 'vue'
import type { GoogleDriveFile } from '../Workspaces/KnowledgeHub/Integrations/GoogleDrive/useGoogleDriveConnection'
import CaseMessageInput from './CaseMessageInput.vue'
import CaseQuery from './CaseQuery.vue'
import CaseResponse from './CaseResponse.vue'
import { isFullCase } from './types'
import { useAgentPicker } from './useAgentPicker'
import { useCasePendingQueryStore } from './useCasePendingQueryStore'
import { useCaseQueryEdit } from './useCaseQueryEdit'
import { useCaseScroll } from './useCaseScroll'
import { useCaseStore } from './useCaseStore'
import { useCaseUploadStore } from './useCaseUploadStore'
import { useChatBubbles } from './useChatBubbles'

const props = defineProps<{
  caseId: string
  workspaceId: string
  agentId?: string
  hubId?: string
}>()

const emit = defineEmits<{
  'select-agent': [string, string[]]
}>()

const caseStore = useCaseStore()

const projectNavigator = useProjectNavigator()
provideProjectNavigationContext(projectNavigator)

const uploadStore = useCaseUploadStore()

const attachments = computed(() => {
  const pendingUploadIds = new Set(pendingStore.pendingQueries.map((q) => q.uploadIds).flat())
  return uploadStore
    .attachmentsForCase(props.caseId)
    .filter((a) => !pendingUploadIds.has(a.uploadId))
})

const { agentId: selectedAgentId } = useAgentPicker()

const selectedHubId = ref<string | undefined>(props.hubId)

syncRefs(toRef(props, 'hubId'), selectedHubId)

const caseId = computed(() => props.caseId)
const messageText = useLocalStorage(caseId, '')

const pendingStore = useCasePendingQueryStore()

const webSearch = ref(false)
watch(caseId, () => {
  webSearch.value = false
  selectedAgentId.value = undefined
  selectedHubId.value = undefined
})

const setPendingMessage = async (text: string) => {
  if (editedMessageId.value) {
    pendingStore.addPendingQuery({
      caseId: props.caseId,
      text,
      agentId: selectedAgentId.value ?? undefined,
      uploadIds: uploadStore.uploads.filter((u) => u.caseId === props.caseId).map((u) => u.id),
      type: 'edit' as const,
      messageId: editedMessageId.value,
      webSearch: webSearch.value,
      hubId: selectedHubId.value,
    })
  } else {
    pendingStore.addPendingQuery({
      caseId: props.caseId,
      text,
      agentId: selectedAgentId.value ?? undefined,
      uploadIds: uploadStore.uploads.filter((u) => u.caseId === props.caseId).map((u) => u.id),
      type: 'create' as const,
      webSearch: webSearch.value,
      hubId: selectedHubId.value,
    })
  }

  editedMessageId.value = null
  messageText.value = ''
  webSearch.value = false

  await nextTick()
  lastQueryRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const deleteAttachment = async (attachmentId: string) => {
  const attachment = uploadStore.attachmentsMap.get(attachmentId)

  if (attachment?.fileId && attachment.status === 'error') {
    // backend only supports deleting files in error or uploading state
    // uploading files currently being uploaded are in an "uploading" state
    // but files with status uploading that got detatched due to tab close, for example
    // will appear in store with an errror status
    await caseStore.deleteActiveCaseFile(props.workspaceId, attachment.fileId)
  }

  uploadStore.removeUpload(attachmentId)

  const pendingQuery = pendingStore.pendingQueries.find((q) => q.uploadIds.includes(attachmentId))
  if (pendingQuery) {
    const idx = pendingQuery?.uploadIds.indexOf(attachmentId)
    if (idx > -1) pendingQuery.uploadIds.splice(idx, 1)
  }
}

const deletePendingQuery = (queryId: string) => {
  const idx = pendingStore.pendingQueries.findIndex((q) => q.id === queryId)
  if (idx > -1) {
    const pendingQuery = pendingStore.pendingQueries[idx]
    invariant(pendingQuery)
    pendingQuery.uploadIds.forEach((id) => uploadStore.removeUpload(id))
    pendingStore.pendingQueries.splice(idx, 1)
  }
}

const { editedMessageId, cancelEdit, editQuery } = useCaseQueryEdit(messageText, selectedAgentId)

const rerunQuery = (queryId: string) => {
  editQuery(queryId)
  setPendingMessage(messageText.value)
}

/**
 * Track the height of the floating input so that we can add an equivalent
 * amount of padding to the scroll container to prevent the input from
 * covering the end of the last message.
 */

const inputRef = useTemplateRef<HTMLElement>('message-input')
const lastQueryRef = ref<HTMLElement | null>(null)
const lastResponseRef = ref<HTMLElement | null>(null)
const scrollContainerRef = useTemplateRef('scroll-container')
const { height: inputHeight } = useElementSize(inputRef)
const { chatPadding, inputPadding } = useCaseScroll({
  containerRef: scrollContainerRef,
  inputRef: inputRef,
  lastQueryRef,
  lastResponseRef,
})

const mainContainerRef = useTemplateRef('mainContainer')
const { isOverDropZone } = useDropzone(mainContainerRef, {
  onAccepted: (files) => uploadStore.addPendingUploads(files, props.caseId),
  supportedMimeTypes: SUPPORTED_MIME_TYPES,
})

const activeCaseMessages = computed(() => {
  return isFullCase(caseStore.activeCase) ? caseStore.activeCase.messages : []
})

const inputPlaceholder = computed(() => {
  return activeCaseMessages.value.length ? 'Ask a follow up' : 'Ask a question'
})

/**
 * Because we can be in an edited state,
 * in which case all messages after the one we're editing are due to be discarded,
 * we compute the actual messages we want to show in chat bubles here.
 */
const bubbleMessageIds = computed(() => {
  const allIds = activeCaseMessages.value.map((m) => m.id)

  // we could have an edited message, meaning it's currently in the input field
  // or we could've set it to dispatch, so it's in pending store
  const editedId =
    editedMessageId.value || pendingStore.pendingQueries.find((q) => q.type === 'edit')?.id

  if (!editedId) {
    return allIds
  }

  const idx = allIds.indexOf(editedId)
  return allIds.slice(idx + 1)
})

const { bubbles } = useChatBubbles(bubbleMessageIds)

const addIntegrationFiles = (files: GoogleDriveFile[]) =>
  uploadStore.addIntegrationFiles(files, props.caseId)

const assignRefIfLastQuery = (el: Element | ComponentPublicInstance | null, index: number) => {
  if (index === bubbles.value.length - 1 || index === bubbles.value.length - 2) {
    const isComponent = el !== null && !(el instanceof Element)
    if (isComponent) {
      lastQueryRef.value = el.$el
    } else {
      lastQueryRef.value = el as HTMLElement | null
    }
  }
}

const assignRefIfLastResponse = (el: Element | ComponentPublicInstance | null, index: number) => {
  if (index === bubbles.value.length - 1) {
    const isComponent = el !== null && !(el instanceof Element)
    if (isComponent) {
      lastResponseRef.value = el.$el
    } else {
      lastResponseRef.value = el as HTMLElement | null
    }
  }
}

const isSendable = computed(() => {
  return (
    caseStore.activeCase?.status === 'ready' &&
    pendingStore.pendingQueries.length === 0 &&
    (messageText.value.length > 0 || attachments.value.length > 0)
  )
})

/**
 * If the active case has an error reason, we give for granted the error happened
 * on the last message.
 */
const getErrorReason = (index: number) => {
  if (index === bubbles.value.length - 1) {
    return caseStore.activeCase?.errorReason ?? null
  }

  return null
}

// We want to follow active messages and set the selected hub to the hub of the most recent query message
watch(activeCaseMessages, () => {
  const mostRecentQuery = activeCaseMessages.value.find((m) => m.type === 'query')
  // Messages might not yet be there, we don't want to reset the hub while loading them
  if (mostRecentQuery) {
    selectedHubId.value = mostRecentQuery.hubId ?? undefined
  }
})

// Whenever we try to edit a message, we want to set the selected hub to the hub of the message
watch(editedMessageId, () => {
  if (!editedMessageId.value) return

  const message = activeCaseMessages.value.find((m) => m.id === editedMessageId.value)
  if (message?.type === 'query') {
    selectedHubId.value = message.hubId ?? undefined
  }
})

/** Suggestions to show above the query input. */
const suggestions = computed<string[]>(() => {
  if (pendingStore.pendingQueries.length > 0) {
    return []
  }

  const orderedMessages = activeCaseMessages.value.toSorted((a, b) =>
    a.updatedAt < b.updatedAt ? -1 : 1,
  )
  const mostRecentMessage = orderedMessages.at(-1)

  if (mostRecentMessage && 'suggestions' in mostRecentMessage) {
    // Sort by length so that shorter ones appear first and are more likely to
    // share the same line.
    return mostRecentMessage.suggestions.toSorted((a, b) => a.length - b.length)
  }

  return []
})

const lastResponseIsVisible = useElementVisibility(lastResponseRef)

const showSuggestions = computed(() => {
  return (
    lastResponseIsVisible.value &&
    suggestions.value.length > 0 &&
    caseStore.activeCase?.status !== 'working'
  )
})
</script>

<template>
  <div
    ref="mainContainer"
    data-test="case-chat"
    class="relative flex min-h-0 flex-col"
  >
    <ScrollGradient
      :container="scrollContainerRef"
      class="inset-x-8 from-background-transparent to-surface-secondary"
      location="top"
      size="md"
    />
    <div
      ref="scroll-container"
      data-test="case-chat-scroll-container"
      :style="{ scrollPaddingBottom: `${inputHeight}px` }"
      class="go-scrollbar relative min-h-0 grow flex-col items-center gap-2 overflow-auto p-8"
    >
      <div
        v-if="isFullCase(caseStore.activeCase)"
        class="go-column flex flex-col gap-2"
        role="log"
        :aria-label="caseStore.activeCase.name ?? 'Chat history'"
      >
        <template
          v-for="(bubble, index) in bubbles"
          :key="bubble.id"
        >
          <CaseQuery
            v-if="bubble.type === 'sent'"
            :ref="(el) => assignRefIfLastQuery(el, index)"
            :message="bubble.message"
            role="listitem"
            class="w-full"
            :disabled="caseStore.activeCase?.status === 'working'"
            @delete-attachment="deleteAttachment"
            @delete-query="deletePendingQuery(bubble.message.id)"
            @edit-query="editQuery(bubble.message.id)"
          />

          <CaseResponse
            v-else-if="bubble.type === 'received'"
            :ref="(el) => assignRefIfLastResponse(el as HTMLElement, index)"
            :disabled="!getErrorReason(index) && caseStore.activeCase?.status === 'working'"
            :messages="bubble.messages"
            :previous-query-id="bubble.previousQueryId"
            role="listitem"
            class="w-full"
            :style="{
              scrollMarginBottom: `${inputHeight || 0}px`,
            }"
            :error-reason="getErrorReason(index)"
            @select-agent="(agentId, entityIds) => emit('select-agent', agentId, entityIds)"
            @rerun-query="rerunQuery"
          />
        </template>
      </div>
      <div
        :style="{ height: `${chatPadding}px` }"
        class="pointer-events-none bg-background-transparent"
      />
    </div>

    <ScrollGradient
      :container="scrollContainerRef"
      class="inset-x-8 shrink-0 from-background-transparent to-surface-secondary"
      location="bottom"
      :style="{ marginBottom: `${inputPadding}px` }"
      size="md"
    />
    <div
      ref="message-input"
      class="absolute inset-x-8 bottom-0 z-1 flex min-w-0 grow justify-center bg-surface-secondary pb-3"
    >
      <div class="go-column w-full shrink">
        <ul
          v-if="showSuggestions"
          class="mb-1 flex max-w-full flex-wrap items-center gap-1"
          aria-label="Suggested queries"
        >
          <li
            v-for="suggestion in suggestions"
            :key="suggestion"
            class="max-w-full"
          >
            <DarwinButton
              class="max-w-full !min-w-0 truncate"
              variant="neutral"
              :title="suggestion"
              rounded
              size="md"
              @click="setPendingMessage(suggestion)"
            >
              <template #leading-icon>
                <div
                  class="flex min-h-5 min-w-5 items-center justify-center rounded-full bg-background-gray-subtlest"
                >
                  <IconSprite
                    icon="idea"
                    size="xs"
                  />
                </div>
              </template>
              <div class="max-w-full min-w-0 truncate">
                {{ suggestion }}
              </div>
            </DarwinButton>
          </li>
        </ul>
        <CaseMessageInput
          v-model:text="messageText"
          v-model:selected-agent-id="selectedAgentId"
          v-model:selected-hub-id="selectedHubId"
          v-model:web-search="webSearch"
          :attachments="attachments"
          :placeholder="inputPlaceholder"
          :is-dropping-files="isOverDropZone"
          :is-editing="!!editedMessageId"
          :disabled="!isSendable"
          @submit="setPendingMessage(messageText)"
          @delete-attachment="deleteAttachment"
          @cancel="cancelEdit"
          @import:library="uploadStore.addLibraryFiles($event, caseId)"
          @import:computer="uploadStore.addPendingUploads($event, caseId)"
          @import:google="addIntegrationFiles"
        />
      </div>
    </div>
  </div>
</template>
